/**
 * The single @supabase/supabase-js client.
 *
 * Created from VITE_ env vars, which are PUBLIC — they are compiled into the
 * bundle (see vite-env.d.ts). The anon key belongs here; SERVICE_ROLE_KEY
 * never does.
 *
 * The client is used for:
 *   - Auth (login, session management, password update) → GoTrue
 *   - Portal-local reads (notifications, preferences) → PostgREST under RLS
 *
 * Business data (OS, orçamentos, laudos, etc.) goes through the BFF — never
 * through this client. See ADR-0004 and ADR-0005.
 *
 * ---------------------------------------------------------------------------
 * Lazy singleton, on purpose.
 *
 * Reading `import.meta.env` and constructing the client used to happen at
 * MODULE LOAD, which meant importing this file — even transitively, e.g. by
 * importing AuthContext.tsx in a test — threw unless Vite env vars were
 * faked first. There was no injection point either, so the first web test
 * ever attempted needed "restructure the auth layer" just to get started.
 *
 * `getSupabaseClient()` defers both the env check and construction to first
 * real use, so importing this module is always side-effect-free. Call sites
 * fetch the client explicitly with `getSupabaseClient()` — there is no
 * client-shaped proxy export; `Object.keys`, spread, `in` and identity all
 * behave normally on the real client because there is no interception layer.
 *
 * `assertSupabaseConfig()` restores the OLD timing of the config check —
 * called once at bootstrap in `main.tsx`, before React renders, a
 * misconfigured deployment fails immediately and loudly, not milliseconds
 * later inside `AuthProvider`'s effect. `getSupabaseClient()` still validates
 * on its own first call too, so any path that reaches it without bootstrap
 * having run (e.g. a test) is still safe.
 *
 * Tests inject a stub with `__setSupabaseClientForTests`, and must call it
 * again with `null` in an `afterEach` to avoid leaking a stub into the next
 * test.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { runtimeEnv } from '../runtimeConfig.ts';

let realClient: SupabaseClient | null = null;
let testClient: SupabaseClient | null = null;

const CONFIG_ERROR =
  'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set at build time. ' +
  'Check compose/env/slot-*.env.';

/**
 * Validates that the required VITE_ env vars are present. Call this once at
 * app bootstrap (`main.tsx`), before rendering, so a misconfigured deploy
 * fails immediately and loudly instead of surfacing later inside an effect.
 */
export function assertSupabaseConfig(): void {
  const supabaseUrl = runtimeEnv('VITE_SUPABASE_URL');
  const supabaseAnonKey = runtimeEnv('VITE_SUPABASE_ANON_KEY');

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(CONFIG_ERROR);
  }
}

function createRealClient(): SupabaseClient {
  const supabaseUrl = runtimeEnv('VITE_SUPABASE_URL');
  const supabaseAnonKey = runtimeEnv('VITE_SUPABASE_ANON_KEY');

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(CONFIG_ERROR);
  }

  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      // The GoTrue API is behind the same origin — Caddy routes /auth/v1/* to Kong.
      // No separate domain, no CORS headaches.
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
    },
  });
}

/**
 * Resolves the client to use: a test stub if one is injected, otherwise the
 * lazily-constructed real client. Throws only when neither is available and
 * construction fails — never on import.
 */
export function getSupabaseClient(): SupabaseClient {
  if (testClient) return testClient;
  if (!realClient) {
    realClient = createRealClient();
  }
  return realClient;
}

/**
 * Test-only injection point. Pass a stub to make `getSupabaseClient()`
 * resolve to it; pass `null` to clear the stub (do this in `afterEach` — a
 * leaked stub silently poisons the next test).
 */
export function __setSupabaseClientForTests(stub: SupabaseClient | null): void {
  testClient = stub;
}

export type { AuthChangeEvent, Session, User } from '@supabase/supabase-js';
